import {
  endOfMonth,
  format,
  isSameDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type {
  DatePreset,
  DateRange,
  DateRangePickerProps,
} from './date-range-picker.types'

const triggerClassNames = {
  base: 'h-9 justify-start text-left font-normal rounded-full px-4',
  border: 'shadow-sm border-input',
  placeholder: 'text-muted-foreground',
}

const getPresets = (): DatePreset[] => {
  const today = new Date()

  return [
    {
      label: 'Last 7 days',
      getValue: () => ({ from: subDays(today, 6), to: today }),
    },
    {
      label: 'Last 14 days',
      getValue: () => ({ from: subDays(today, 13), to: today }),
    },
    {
      label: 'Last 30 days',
      getValue: () => ({ from: subDays(today, 29), to: today }),
    },
    {
      label: 'Last 90 days',
      getValue: () => ({ from: subDays(today, 89), to: today }),
    },
    {
      label: 'This month',
      getValue: () => ({ from: startOfMonth(today), to: today }),
    },
    {
      label: 'Last month',
      getValue: () => {
        const lastMonth = subMonths(today, 1)
        return {
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        }
      },
    },
  ]
}

const formatDateRange = (range: DateRange | undefined): string => {
  if (!range?.from) {
    return ''
  }

  if (!range.to) {
    return format(range.from, 'LLL dd, y')
  }

  return `${format(range.from, 'LLL dd, y')} - ${format(range.to, 'LLL dd, y')}`
}

function DateRangePicker({
  value,
  onChange,
  placeholder = 'Pick a date range',
  className,
  align = 'start',
  disabled = false,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(value)

  useEffect(() => {
    setDraftRange(value)
  }, [value])

  const handleDayClick = (day: Date) => {
    const { from, to } = draftRange ?? {}

    if (from && isSameDay(day, from)) {
      setDraftRange({ from: undefined, to })
      return
    }

    if (to && isSameDay(day, to)) {
      setDraftRange({ from, to: undefined })
      return
    }

    if (!from) {
      if (to && day > to) {
        setDraftRange({ from: to, to: day })
      } else {
        setDraftRange({ from: day, to })
      }
      return
    }

    if (!to) {
      if (day < from) {
        setDraftRange({ from: day, to: from })
      } else {
        setDraftRange({ from, to: day })
      }
      return
    }

    setDraftRange({ from: day, to: undefined })
  }

  const handleSelect = () => {}

  const displayValue = formatDateRange(value)
  const presets = getPresets()

  const handlePresetClick = (preset: DatePreset) => {
    const range = preset.getValue()
    onChange?.(range)
    setOpen(false)
  }

  const handleApply = () => {
    onChange?.(draftRange)
    setOpen(false)
  }

  const handleCancel = () => {
    setDraftRange(value)
    setOpen(false)
  }

  const hasChanges =
    draftRange?.from?.getTime() !== value?.from?.getTime() ||
    draftRange?.to?.getTime() !== value?.to?.getTime()

  const canApply = draftRange?.from && draftRange?.to

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            triggerClassNames.base,
            triggerClassNames.border,
            !value && triggerClassNames.placeholder,
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 rounded-2xl overflow-visible"
        align={align}
      >
        <div className="flex overflow-visible">
          <div className="flex flex-col gap-1 border-r p-3 w-36">
            {presets.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="justify-start h-8 px-2 font-normal"
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <div className="relative flex flex-col overflow-visible">
            <Calendar
              mode="range"
              defaultMonth={draftRange?.from}
              selected={draftRange}
              onDayClick={handleDayClick}
              onSelect={handleSelect}
              numberOfMonths={2}
            />
            <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={!canApply || !hasChanges}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DateRangePicker, formatDateRange, triggerClassNames }
export type { DateRangePickerProps, DateRange }
